import { Button } from '@unified-lk/ui'
import { formatCurrency, formatDate } from '../../lib/format'
import type { StaffPatientsListData } from '../../types/plans'
import { StaffMetricCard } from '../../shared/staff-ui'
import { WorkspacePagination, WorkspaceSearchPanel } from '../shared/WorkspaceListControls'

function genderShort(value: string | null | undefined) {
  if (!value) return '—'
  const normalized = value.toLowerCase()
  if (normalized.startsWith('m') || normalized.includes('муж')) return 'M'
  if (normalized.startsWith('f') || normalized.includes('жен')) return 'F'
  return value.slice(0, 1).toUpperCase()
}

export function PatientListSection({
  data,
  loading,
  error,
  searchValue,
  selectedPatientId,
  onSearchChange,
  onSearchSubmit,
  onSelectPatient,
  onRetry,
  onPrevPage,
  onNextPage,
}: {
  data: StaffPatientsListData | null
  loading: boolean
  error: string | null
  searchValue: string
  selectedPatientId: number | null
  onSearchChange: (value: string) => void
  onSearchSubmit: () => void
  onSelectPatient: (patientId: number) => void
  onRetry: () => void
  onPrevPage: () => void
  onNextPage: () => void
}) {
  if (loading && !data) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">Загружаем список пациентов...</div>
  }

  const phonesHidden = (data?.patients ?? []).some((patient) => patient.phone_hidden)

  return (
    <div className="grid gap-4">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Пациенты</h2>
            <p className="mt-1 text-sm text-slate-500">
              {data?.summary.total?.toLocaleString('ru-RU') ?? 0} пациентов в базе
            </p>
          </div>
          <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
          <StaffMetricCard label="Всего" value={data?.summary.total ?? 0} accent="blue" />
          <StaffMetricCard label="Загружено" value={data?.summary.loaded ?? 0} accent="green" />
          <StaffMetricCard label="С телефонами" value={data?.summary.with_phone ?? 0} accent="purple" />
          <StaffMetricCard
            label="С долгом"
            value={<span className="text-rose-600">{data?.summary.debtors ?? 0}</span>}
            accent="rose"
          />
        </div>

        <WorkspaceSearchPanel
          label={phonesHidden ? 'Поиск по имени' : 'Поиск по имени или телефону'}
          value={searchValue}
          onChange={onSearchChange}
          onSubmit={onSearchSubmit}
          placeholder={phonesHidden ? 'Например, Смирнов' : 'Поиск по имени или телефону...'}
        />
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {error ? <div className="border-b border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

        <div className="hidden grid-cols-[72px_minmax(0,1.5fr)_minmax(0,1fr)_120px_minmax(0,1fr)_120px] gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 lg:grid">
          <span>#</span>
          <span>Пациент</span>
          <span>Телефон</span>
          <span>Последний визит</span>
          <span>Врач</span>
          <span className="text-right">Долг</span>
        </div>

        <div className="divide-y divide-slate-100">
          {(data?.patients ?? []).map((patient) => {
            const debt = patient.debt_amount ?? 0
            const active = selectedPatientId === patient.id
            return (
              <button
                key={patient.id}
                type="button"
                onClick={() => onSelectPatient(patient.id)}
                className={`grid w-full gap-3 px-4 py-4 text-left transition hover:bg-slate-50 lg:grid-cols-[72px_minmax(0,1.5fr)_minmax(0,1fr)_120px_minmax(0,1fr)_120px] lg:items-center ${active ? 'bg-blue-50/70' : 'bg-white'}`}
              >
                <span className="text-sm text-slate-400">{patient.id}</span>
                <span>
                  <strong className="block text-sm text-slate-900">{patient.full_name}</strong>
                  <small className="text-xs text-slate-500">
                    {genderShort(patient.gender)} · {formatDate(patient.birth_date)}
                  </small>
                </span>
                <span className="text-sm text-slate-700">
                  {patient.phone_hidden ? 'Скрыт правами' : (patient.phone ?? '—')}
                </span>
                <span className="text-sm text-slate-700">{formatDate(patient.last_visit_date)}</span>
                <span className="text-sm text-slate-700">{patient.last_doctor_name ?? '—'}</span>
                <span className={`text-sm font-semibold lg:text-right ${debt > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                  {formatCurrency(debt)}
                </span>
              </button>
            )
          })}
        </div>

        <div className="border-t border-slate-100 px-4 py-3">
          <WorkspacePagination
            page={data?.filters.page ?? 1}
            pages={Math.max(data?.pages ?? 1, 1)}
            total={data?.total ?? 0}
            pageSize={data?.filters.per_page ?? undefined}
            itemLabel="пациентов"
            onPrevPage={onPrevPage}
            onNextPage={onNextPage}
          />
        </div>
      </section>
    </div>
  )
}
